import { toCrystalKind, getEmpyrealCrystalStackLimit, isEmpyrealCrystalItem, isSameCrystalKind, type CarriedInventorySlots } from './crystal-utils';
import type { EmpyrealCrystalKind, EmpyrealCrystalSize, EmpyrealCrystalStack } from './types/crystals';
import { getRecipeSystem } from './recipe-system';
import { hasMinimumEssence, resolveSpendableCraftEssence, withEssenceAdded, type CrystalCoalescence, type EssencePool, type RecipeDefinition } from './types/essence';

/** How long each crystal size takes to coalesce. */
export const CRYSTAL_COALESCENCE_MS: Readonly<Record<EmpyrealCrystalSize, number>> = {
    splinter: 5000,
    fragment: 20000,
    complete: 60000,
};

/** What a carried slot holds, as far as stacking crystals goes: nothing, a crystal stack, or anything else (gear). */
export type CrystalSlotOccupant = EmpyrealCrystalStack | 'other' | undefined;

/**
 * Whether a slot holding `occupant` takes one more crystal of `kind` once
 * the crystals in `pending` (already coalescing into it) have landed: every
 * one must stack with what is there, and the stack stay within its limit.
 * The one coalescing room rule; the host checks it over the carried
 * inventory, the Coalesce section over the slot it shows.
 */
export function canCoalesceIntoOccupant(occupant: CrystalSlotOccupant, pending: readonly EmpyrealCrystalKind[], kind: EmpyrealCrystalKind): boolean {
    if (occupant === 'other') {
        return false;
    }
    const base = occupant ?? pending[0] ?? kind;
    const arriving = [...pending, kind];
    return arriving.every((candidate) => isSameCrystalKind(candidate, base))
        && (occupant?.quantity ?? 0) + arriving.length <= getEmpyrealCrystalStackLimit(base.size);
}

/**
 * Whether one more crystal of this kind will fit in carried slot `slotIndex`
 * once it finishes coalescing, counting crystals already coalescing into
 * that slot as if they had landed. A coalescence isn't started without room.
 */
export function hasRoomToCoalesceIntoSlot(
    inventory: CarriedInventorySlots,
    totalSlots: number,
    pending: readonly Pick<CrystalCoalescence, 'targetSlotIndex' | 'recipe'>[],
    slotIndex: number,
    kind: EmpyrealCrystalKind,
): boolean {
    if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= totalSlots) {
        return false;
    }
    const item = inventory[slotIndex];
    const occupant: CrystalSlotOccupant = item === undefined || item === null ? undefined : (isEmpyrealCrystalItem(item) ? item : 'other');
    const arriving = pending
        .filter((coalescence) => coalescence.targetSlotIndex === slotIndex)
        .map((coalescence) => toCrystalKind(coalescence.recipe));
    return canCoalesceIntoOccupant(occupant, arriving, kind);
}

/** Whether a coalescence may start: the recipe it starts, or why it is refused, in the player's words. */
export type CoalescenceStartCheck =
    | { readonly ok: true; readonly recipe: RecipeDefinition }
    | { readonly ok: false; readonly reason: string };

/** The essence a coalescence draws on (see `resolveSpendableCraftEssence`). */
export interface CoalescenceEssence {
    readonly run: EssencePool;
    /** What the run's commission holds back (`resolveCommissionReservedEssence`). */
    readonly held: Partial<EssencePool>;
    readonly banked: Partial<EssencePool>;
}

/**
 * Whether recipe `recipeId` may start coalescing into carried slot
 * `slotIndex`: a recipe known from the start, room in the slot
 * (`hasRoomFor`, the caller's `hasRoomToCoalesceIntoSlot` over its slots),
 * and the essence to pay for it, saying when what is short is held for the
 * commission. The host judges a start by it, and the debug overlay's
 * stand-in host too.
 */
export function checkCoalescenceStart(
    recipeId: string,
    slotIndex: number,
    hasRoomFor: (kind: EmpyrealCrystalKind) => boolean,
    essence: CoalescenceEssence,
): CoalescenceStartCheck {
    const recipe = getRecipeSystem().getRecipe(recipeId);
    if (!recipe || !recipe.isDiscoveredByDefault) {
        return { ok: false, reason: 'Unknown recipe.' };
    }
    if (!hasRoomFor(toCrystalKind(recipe))) {
        return { ok: false, reason: `No room for that crystal in carried slot ${slotIndex + 1}.` };
    }
    if (!hasMinimumEssence(resolveSpendableCraftEssence(essence.run, essence.held, essence.banked), recipe.ingredients)) {
        return {
            ok: false,
            reason: hasMinimumEssence(withEssenceAdded(essence.run, essence.banked), recipe.ingredients)
                ? `Not enough essence for ${recipe.name}. Some of what you carry is held for your commission.`
                : `Not enough essence for ${recipe.name}.`,
        };
    }
    return { ok: true, recipe };
}
